import { useCandidate, useJob, useUpdateCandidateStatus } from "@vidario/api-client";
import { maskCandidate, showsReplacement, type Candidate } from "@vidario/domain";
import { Breadcrumb, Button, Card, StrengthsWeaknesses, useToast, cn } from "@vidario/ui";
import { XCircle } from "lucide-react";
import { useState, type ReactElement } from "react";
import { useNavigate, useParams } from "react-router";

import { CLASSES_NOTE } from "../../anonym/copy";
import { ANONYM_OUTLINE } from "../../anonym/outline";
import { useAnonymView } from "../../anonym/useAnonymView";
import { useRole } from "../../guards/useRole";
import { ActionsSidebar } from "./ActionsSidebar";
import { CareerCard } from "./CareerCard";
import { EducationCard } from "./EducationCard";
import { LanguagesCard } from "./LanguagesCard";
import { ContactSidebar } from "./ContactSidebar";
import { InterviewCard } from "./InterviewCard";
import { NotesCard } from "./NotesCard";
import { RejectDialog } from "./RejectDialog";
import { SkillMatchCard } from "./SkillMatchCard";

/** Candidate-Detail screen (MVP §4.1-4.8). From xl, 3 columns:
 *   - left: identity + score + status actions (sticky)
 *   - center: Stärken/Schwächen, detail scores, skill match, career,
 *     notes, admin-only interview block
 *   - right: contact, attachments, audit log (sticky)
 * From lg, 2 columns: actions, then content with contact under it.
 * Below lg, 1 column: actions, content, contact. The screen follows the DOM
 * order at every width, so focus and reading order match it (#124). */
const DETAIL_GRID = cn(
  "grid grid-cols-1 items-start gap-6 [grid-template-areas:'actions'_'content'_'contact']",
  "lg:grid-cols-[300px_minmax(0,1fr)] lg:[grid-template-areas:'actions_content'_'._contact']",
  "xl:grid-cols-[300px_minmax(18rem,1fr)_280px] xl:[grid-template-areas:'actions_content_contact']",
);
// Sticky under the TopNav, and under the Anonym-Ansicht bar when it shows; only
// in 3 columns, where each aside has its column to itself.
const STICKY_ASIDE = "xl:sticky xl:top-[calc(5.5rem+var(--anonym-bar-offset,0px))]";

export function CandidateDetailRoute(): ReactElement {
  const { jobId = "", candidateId = "" } = useParams<{
    jobId: string;
    candidateId: string;
  }>();
  const navigate = useNavigate();
  const role = useRole();
  const isAdmin = role === "admin";
  const toast = useToast();

  const { data: job } = useJob(jobId);
  const { data: candidate, isLoading, error } = useCandidate(jobId, candidateId);
  const updateStatus = useUpdateCandidateStatus();

  const [rejectOpen, setRejectOpen] = useState(false);
  const anonym = useAnonymView();

  if (isLoading) {
    return (
      <main className="mx-auto max-w-[var(--container-max)] px-4 py-10 sm:px-8">
        <p className="text-sm text-text-secondary" role="status">
          Lade Kandidat…
        </p>
      </main>
    );
  }
  if (error || !candidate || !job) {
    return (
      <main className="mx-auto max-w-[var(--container-max)] px-4 py-10 sm:px-8">
        <p className="text-sm text-danger-600" role="alert">
          Kandidat konnte nicht geladen werden.
        </p>
      </main>
    );
  }

  const c: Candidate = candidate;
  // What this view shows: the Anonym-Ansicht masks it (#140). Children get the
  // real candidate for their edit drafts and mask their own display.
  const { candidate: shown, hidden } = maskCandidate(c, anonym);

  const onReject = (reason: string): void => {
    updateStatus.mutate(
      { jobId, candidateId: c.id, status: "rejected", rejectionReason: reason },
      {
        onSuccess: () => {
          toast.push({
            kind: "warning",
            title: "Kandidat abgelehnt",
            message: "Status auf „Abgelehnt“ gesetzt.",
          });
          setRejectOpen(false);
        },
        onError: (err) => toast.push({ kind: "error", title: "Fehler", message: err.message }),
      },
    );
  };

  const onUnreject = (): void => {
    updateStatus.mutate(
      { jobId, candidateId: c.id, status: "longlist", rejectionReason: null },
      {
        onSuccess: () =>
          toast.push({
            kind: "success",
            title: "Ablehnung aufgehoben",
            message: "Zurück in der Longlist.",
          }),
        onError: (err) => toast.push({ kind: "error", title: "Fehler", message: err.message }),
      },
    );
  };

  const strengthsHidden = Boolean(
    hidden &&
    (hidden.strengths || hidden.weaknesses) &&
    (c.strengths.length > 0 || c.weaknesses.length > 0),
  );
  const hasStrengthsOrWeaknesses =
    shown.strengths.length > 0 || shown.weaknesses.length > 0 || strengthsHidden;

  // A closed job's candidates are read-only apart from notes (#19).
  const readOnly = job.status === "closed";

  return (
    <main className="mx-auto max-w-[var(--container-max)] px-4 py-10 sm:px-8 pb-20">
      <Breadcrumb
        className="mb-5"
        items={[
          { label: "Dashboard", onClick: () => void navigate("/dashboard") },
          {
            label: job.title,
            onClick: () => void navigate(`/jobs/${jobId}/longlist`),
          },
          { label: shown.name },
        ]}
      />

      {c.status === "rejected" && (
        <div
          className="mb-5 flex gap-3.5 rounded-xl border border-[#FCA5A5] bg-danger-50 p-4"
          role="alert"
          data-testid="rejection-banner"
        >
          <XCircle size={20} className="mt-0.5 shrink-0 text-danger-500" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-danger-700">Kandidat wurde abgelehnt</div>
            {shown.rejectionReason && (
              <div className="mt-1 text-[13px] text-danger-700">{shown.rejectionReason}</div>
            )}
          </div>
          {/* A closed job's candidates are frozen (#19). */}
          {job.status !== "closed" && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onUnreject}
              loading={updateStatus.isPending}
            >
              Ablehnung aufheben
            </Button>
          )}
        </div>
      )}

      <div
        data-testid="candidate-detail-grid"
        className={cn(DETAIL_GRID, anonym && ANONYM_OUTLINE)}
      >
        <aside className={cn(STICKY_ASIDE, "[grid-area:actions]")}>
          <ActionsSidebar
            key={`actions-${c.id}`}
            jobId={jobId}
            candidate={c}
            job={job}
            isAdmin={isAdmin}
            onReject={() => setRejectOpen(true)}
            rejecting={updateStatus.isPending}
          />
        </aside>

        {/* Long words wrap inside the cards instead of widening the column (#148). */}
        <div className="flex min-w-0 flex-col gap-5 wrap-break-word [grid-area:content]">
          {hasStrengthsOrWeaknesses && (
            <Card>
              <h3 className="m-0 mb-3.5 text-[15px] font-semibold">Stärken &amp; Schwächen</h3>
              <StrengthsWeaknesses
                strengths={shown.strengths}
                weaknesses={shown.weaknesses}
                strengthsHidden={Boolean(hidden?.strengths && c.strengths.length > 0)}
                weaknessesHidden={Boolean(hidden?.weaknesses && c.weaknesses.length > 0)}
              />
              {showsReplacement(
                [...c.strengths, ...c.weaknesses],
                [...shown.strengths, ...shown.weaknesses],
              ) && <p className="m-0 mt-3 text-xs text-text-tertiary">{CLASSES_NOTE}</p>}
            </Card>
          )}

          <SkillMatchCard jobId={jobId} job={job} candidate={c} canEdit={!readOnly} />

          <CareerCard candidate={c} />

          <EducationCard candidate={c} />

          <LanguagesCard candidate={c} />

          <NotesCard key={`notes-${c.id}`} jobId={jobId} candidate={c} isAdmin={isAdmin} />

          {isAdmin && (
            <InterviewCard
              key={`interview-${c.id}`}
              jobId={jobId}
              candidate={c}
              findingsEditable={!readOnly}
            />
          )}
        </div>

        <aside className={cn(STICKY_ASIDE, "[grid-area:contact]")}>
          <ContactSidebar
            key={`contact-${c.id}`}
            readOnly={readOnly}
            jobId={jobId}
            candidate={c}
            isAdmin={isAdmin}
          />
        </aside>
      </div>

      <RejectDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        onConfirm={onReject}
        loading={updateStatus.isPending}
      />
    </main>
  );
}
